import { useState } from "react";
import { motion } from "framer-motion";
import { Shield, Sparkles, Check, ArrowLeft, Droplet, Award, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import QuoteWizard from "@/components/landing/QuoteWizard";

const PACKAGES = [
  {
    id: "ceramic-seal",
    name: "Ceramic Seal",
    sub: "Gateway to ceramic",
    duration: "6–12 month protection",
    price: "from $399",
    color: "from-slate-700 to-slate-900",
    badge: "ENTRY",
    icon: Droplet,
    features: [
      "Single-stage paint enhancement",
      "Pro-grade SiO₂ ceramic sealant",
      "Hydrophobic water beading",
      "UV protection layer",
      "Glass + wheel sealant included",
      "6–12 month durability",
    ],
    description:
      "The smart-money first ceramic. A real liquid ceramic sealant — not a wax — bonded over a single-stage enhancement. You get the gloss, the water-beading, and the easier washing without the multi-day install.",
  },
  {
    id: "ceramic-coating",
    name: "Ceramic Coating",
    sub: "The flagship",
    duration: "2–5 year protection",
    price: "from $899",
    color: "from-sky-600 to-sky-900",
    badge: "PREMIUM",
    icon: ShieldCheck,
    features: [
      "Multi-stage paint correction included",
      "Pro-installer ceramic (9H hardness)",
      "Self-healing top coat",
      "Extreme hydrophobic + anti-static",
      "Wheel face, glass, plastic, interior packages available",
      "2-, 3-, or 5-year warranty",
      "Annual maintenance package included",
    ],
    description:
      "The full pro install. We correct the paint to a flaw-free finish first, then layer a professional ceramic that bonds at the molecular level. The result: a glass-smooth, ultra-glossy, hydrophobic surface that lasts years.",
  },
];

const COMPARE_ROWS = [
  ["Durability", "6–12 months", "2–5 years"],
  ["Paint Correction", "Single-stage enhancement", "Multi-stage correction"],
  ["Ceramic Layer", "Sealant grade", "Pro 9H coating"],
  ["Self-Healing", "—", "Yes (top coat)"],
  ["Warranty", "Limited 6 mo.", "Up to 5 years"],
  ["Install Time", "1 day", "1–2 days"],
];

export default function CeramicPage() {
  const navigate = useNavigate();
  const [wizardOpen, setWizardOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header onQuoteClick={() => setWizardOpen(true)} />

      <main className="relative z-10">
        {/* Hero */}
        <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 overflow-hidden">
          <div className="absolute top-20 right-1/4 w-96 h-96 bg-sky-500/15 rounded-full blur-[140px]" />
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-[140px]" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-400 hover:text-sky-300 transition-colors mb-6 text-sm"
              data-testid="ceramic-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <span className="text-sky-500 text-xs tracking-[0.3em] uppercase mb-4 block">Ceramic Protection</span>
              <h1 className="font-['Outfit'] text-4xl sm:text-6xl lg:text-7xl text-white font-light leading-tight">
                Glass-Smooth.
                <br />
                <span className="gradient-text font-medium">Showroom Locked.</span>
              </h1>
              <p className="text-gray-300 text-base sm:text-lg mt-6 max-w-2xl leading-relaxed">
                We offer two ceramic tiers — entry-level <span className="text-white">Ceramic Seal</span> for fast,
                affordable protection, and our flagship <span className="text-white">Ceramic Coating</span> with paint
                correction for years of glass-like finish.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Packages */}
        <section className="py-12 sm:py-16 bg-[#080808]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
              {PACKAGES.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative rounded-2xl overflow-hidden border ${p.id === "ceramic-coating" ? "border-sky-500/40" : "border-white/10"} bg-gradient-to-br ${p.color} p-6 sm:p-8`}
                  data-testid={`ceramic-package-${p.id}`}
                >
                  {p.id === "ceramic-coating" && (
                    <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-sky-500 text-white text-[10px] uppercase tracking-widest font-bold">
                      Most Popular
                    </div>
                  )}

                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center shrink-0">
                      <p.icon className="w-6 h-6 text-sky-300" />
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-sky-300/80 font-semibold">
                        {p.badge}
                      </span>
                      <h2 className="font-['Outfit'] text-2xl sm:text-3xl text-white font-medium">{p.name}</h2>
                      <p className="text-gray-300 text-sm">{p.sub} · {p.duration}</p>
                    </div>
                  </div>

                  <p className="text-gray-200 text-sm leading-relaxed mb-5">{p.description}</p>

                  <ul className="space-y-2 mb-6">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-gray-200">
                        <Check className="w-4 h-4 text-sky-300 shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center justify-between gap-3 pt-5 border-t border-white/10">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-gray-400">Starting</p>
                      <p className="font-['Outfit'] text-3xl text-white">{p.price}</p>
                    </div>
                    <button
                      onClick={() => setWizardOpen(true)}
                      className="px-5 py-3 rounded-full font-medium text-sm transition-colors bg-sky-500 hover:bg-sky-400 text-white"
                      data-testid={`ceramic-book-${p.id}`}
                    >
                      Book {p.name}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="py-16 sm:py-20 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl text-white text-center mb-8 sm:mb-10">
              Side by <span className="gradient-text">Side</span>
            </h2>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <div className="grid grid-cols-3 px-4 sm:px-6 py-4 border-b border-white/10 bg-white/[0.03]">
                <div className="text-xs uppercase tracking-widest text-gray-500">Feature</div>
                <div className="text-xs uppercase tracking-widest text-gray-400 text-center">Ceramic Seal</div>
                <div className="text-xs uppercase tracking-widest text-sky-300 text-center">Ceramic Coating</div>
              </div>
              {COMPARE_ROWS.map((row) => (
                <div
                  key={row[0]}
                  className="grid grid-cols-3 px-4 sm:px-6 py-4 border-b border-white/5 last:border-0 items-center"
                >
                  <div className="text-sm text-gray-200">{row[0]}</div>
                  <div className="text-sm text-gray-400 text-center">{row[1]}</div>
                  <div className="text-sm text-white text-center">{row[2]}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-16 sm:py-20 bg-[#080808]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <Award className="w-12 h-12 text-sky-400 mx-auto mb-4" />
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl text-white mb-3">
              Not sure which one is right?
            </h2>
            <p className="text-gray-400 mb-6">
              Upload a couple of photos of your vehicle and our AI will recommend the best ceramic tier for your paint condition and budget.
            </p>
            <button
              onClick={() => setWizardOpen(true)}
              data-testid="ceramic-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-400 text-white font-medium"
            >
              <Sparkles className="w-4 h-4" /> Get AI Recommendation
            </button>
          </div>
        </section>
      </main>

      <Footer />
      <QuoteWizard open={wizardOpen} onClose={() => setWizardOpen(false)} to="/book/ceramic_coating" />
    </div>
  );
}
